import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useState } from "react";

import { CredentialsDialog } from "@/components/CredentialsDialog.tsx";
import { ApiError, api } from "@/lib/api.ts";
import type { TenantCreatedOut, TenantStatus } from "@/lib/types.ts";

const input =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900";

/** Alta de empresa: dueño y, opcionalmente, primera sucursal lista para vender
 *  (series de boleta y factura asignadas solas, cajas y servidor local). */
export function NewTenantDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    ruc: "",
    legal_name: "",
    trade_name: "",
    plan: "free",
    status: "trial" as TenantStatus,
    admin_username: "admin",
    admin_password: "",
    admin_full_name: "Administrador",
  });
  const [withBranch, setWithBranch] = useState(true);
  const [branch, setBranch] = useState({
    code: "PRINCIPAL",
    name: "Principal",
    address: "",
    terminals: 1,
    local_server: false,
  });
  const [result, setResult] = useState<TenantCreatedOut | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.post<TenantCreatedOut>("/tenants", {
        ...form,
        trade_name: form.trade_name || null,
        first_branch: withBranch ? { ...branch, address: branch.address || null } : null,
      }),
    onSuccess: (out) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["metrics"] });
      setResult(out);
    },
  });
  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  if (result?.node_env) {
    return (
      <CredentialsDialog
        title={`${result.tenant.legal_name}: servidor local de ${result.first_branch?.code ?? ""}`}
        env={result.node_env}
        onClose={onClose}
      />
    );
  }
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
        <div className="w-full max-w-md space-y-3 rounded-2xl bg-white p-6 shadow-xl">
          <h3 className="text-lg font-semibold text-slate-900">Empresa creada</h3>
          <p className="text-sm text-slate-600">
            {result.tenant.legal_name} (RUC {result.tenant.ruc}). El dueño entra a la PWA con el
            usuario <span className="font-mono">{form.admin_username}</span>.
          </p>
          {result.first_branch && (
            <p className="text-sm text-slate-600">
              Sucursal {result.first_branch.code} lista para vender: boletas{" "}
              <span className="font-mono">{result.first_branch.boleta_series}</span>, facturas{" "}
              <span className="font-mono">{result.first_branch.factura_series}</span>,{" "}
              {result.first_branch.terminals} caja(s). Falta cargar productos y cajeros: la consola
              de la empresa lo guía.
            </p>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Listo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Nueva empresa</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-lg p-1 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input {...field("ruc")} placeholder="RUC" maxLength={11} required className={input} />
          <select {...field("status")} className={input} aria-label="Estado">
            <option value="trial">Prueba</option>
            <option value="active">Activa</option>
          </select>
        </div>
        <input {...field("legal_name")} placeholder="Razón social" required className={input} />
        <input {...field("trade_name")} placeholder="Nombre comercial (opcional)" className={input} />
        <input {...field("plan")} placeholder="Plan" className={input} />

        <p className="pt-2 text-xs font-medium text-slate-500 uppercase">Dueño (primer usuario)</p>
        <div className="grid grid-cols-2 gap-3">
          <input {...field("admin_username")} placeholder="Usuario" required className={input} />
          <input
            {...field("admin_password")}
            type="password"
            placeholder="Contraseña (mín. 8)"
            minLength={8}
            required
            className={input}
          />
        </div>
        <input {...field("admin_full_name")} placeholder="Nombre" className={input} />

        <label className="flex items-center gap-2 pt-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={withBranch}
            onChange={(e) => setWithBranch(e.target.checked)}
            className="h-4 w-4"
          />
          Dejarla lista para vender (sucursal, series de boleta y factura, cajas)
        </label>
        {withBranch && (
          <div className="space-y-3 rounded-lg bg-slate-50 p-3">
            <div className="grid grid-cols-3 gap-3">
              <input
                value={branch.code}
                onChange={(e) => setBranch((b) => ({ ...b, code: e.target.value }))}
                placeholder="Código"
                required
                className={input}
              />
              <input
                value={branch.name}
                onChange={(e) => setBranch((b) => ({ ...b, name: e.target.value }))}
                placeholder="Nombre"
                required
                className={input + " col-span-2"}
              />
            </div>
            <input
              value={branch.address}
              onChange={(e) => setBranch((b) => ({ ...b, address: e.target.value }))}
              placeholder="Dirección (opcional)"
              className={input}
            />
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
              <label className="flex items-center gap-2">
                Cajas
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={branch.terminals}
                  onChange={(e) => setBranch((b) => ({ ...b, terminals: Number(e.target.value) }))}
                  className="w-16 rounded-lg border border-slate-300 px-2 py-1"
                />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={branch.local_server}
                  onChange={(e) => setBranch((b) => ({ ...b, local_server: e.target.checked }))}
                  className="h-4 w-4"
                />
                Con servidor local (edición offline)
              </label>
            </div>
            <p className="text-xs text-slate-500">Las series se asignan solas (B001, F001, ...).</p>
          </div>
        )}
        {create.error && (
          <p className="text-sm text-red-700">
            {create.error instanceof ApiError ? create.error.message : "No se pudo crear"}
          </p>
        )}
        <button
          type="submit"
          disabled={create.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Crear empresa
        </button>
      </form>
    </div>
  );
}
